import { useEffect, useRef, useState, type FormEvent } from "react";
import { readChatHistory, sendChat, type ChatTurn } from "../api/chat";
import { ApiError, messageOf } from "../api/http";
import type { RunProjection } from "../features/ops/investigationProjection";
import { Notice } from "./chrome";
import { useReadOnly } from "./readOnly";

const MAX_CHARS = 12_000;

/** The question is REST state; the answer is only this turn's durable segment. */
export function ChatPanel({
  runId,
  projection,
}: {
  runId: string;
  projection: RunProjection;
}) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [reload, setReload] = useState(0);
  const readOnly = useReadOnly();
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const olderLoaded = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const page = await readChatHistory(runId);
        if (!live) return;
        setTurns((current) => merge(current, page.turns));
        if (!olderLoaded.current) setCursor(page.next_cursor);
        setReadError(null);
      } catch (reason) {
        if (live) setReadError(readFailure(reason));
      } finally {
        if (live) {
          setLoading(false);
          timer = setTimeout(() => void poll(), 4000);
        }
      }
    }
    void poll();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [runId, reload]);

  async function earlier() {
    if (!cursor || paging) return;
    setPaging(true);
    try {
      const page = await readChatHistory(runId, cursor);
      if (!mounted.current) return;
      olderLoaded.current = true;
      setTurns((current) => merge(current, page.turns));
      setCursor(page.next_cursor);
      setReadError(null);
    } catch (reason) {
      if (mounted.current) setReadError(readFailure(reason));
    } finally {
      if (mounted.current) setPaging(false);
    }
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (inFlight.current) return;
    const message = draft.trim();
    if (!message || [...message].length > MAX_CHARS) {
      setError(
        !message
          ? "Enter a question before sending."
          : "Keep your question to 12,000 characters or fewer.",
      );
      document.getElementById("chat-question")?.focus();
      return;
    }
    inFlight.current = true;
    setSending(true);
    setError(null);
    try {
      const turn = await sendChat(runId, message);
      if (!mounted.current) return;
      setTurns((current) => merge(current, [turn]));
      setDraft("");
      setReload((value) => value + 1);
    } catch (reason) {
      if (!mounted.current) return;
      const ambiguous = !(reason instanceof ApiError) || reason.status >= 500;
      setError(
        ambiguous
          ? "Your question may have been saved, but sending it was not confirmed. Check the conversation before choosing to send again; sending again asks it twice."
          : readFailure(reason),
      );
      setReload((value) => value + 1);
    } finally {
      inFlight.current = false;
      if (mounted.current) setSending(false);
    }
  }
  return (
    <div className="brief-chat stack">
      <header>
        <p className="eyebrow">Ask about this investigation</p>
        <h2>Conversation</h2>
        <p className="muted">
          Questions and replies stay with this investigation. A reply
          does not change its original conclusion or resolve the incident.
        </p>
      </header>
      {loading ? <p role="status">Reading conversation…</p> : null}
      {readError ? (
        <div className="stack stack--tight">
          <Notice tone="bad">{readError}</Notice>
          <button
            className="btn btn--quiet"
            onClick={() => setReload((value) => value + 1)}
          >
            Read conversation again
          </button>
        </div>
      ) : null}
      {!loading && !readError && !turns.length ? (
        <p>No questions yet. Ask about the evidence or the next step.</p>
      ) : null}
      {cursor ? (
        <button
          className="btn btn--quiet"
          disabled={paging}
          onClick={() => void earlier()}
        >
          {paging ? "Reading earlier questions…" : "Read earlier questions"}
        </button>
      ) : null}
      <ol className="brief-chat-turns" aria-label="Conversation so far">
        {turns.map((turn) => {
          const rows = projection.timeline.filter(
            (row) =>
              row.attempt_token?.slice(
                0,
                row.attempt_token.lastIndexOf(":"),
              ) === turn.segment,
          );
          const terminal = [...rows]
            .reverse()
            .find((row) =>
              ["complete", "failed", "budget_exhausted"].includes(row.type),
            );
          const answer =
            terminal?.type === "complete" &&
            typeof terminal.payload.answer === "string"
              ? terminal.payload.answer
              : null;
          // The API reads a turn's state off its segment's exit row (#1396), which this
          // projection may not hold; its rows decide only a turn the API reads as queued.
          const exited = turn.exit_type !== null;
          const state = exited
            ? turn.state === "answered"
              ? "Answered"
              : "Reply stopped"
            : terminal
              ? terminal.type === "complete"
                ? "Answered"
                : "Reply stopped"
              : rows.length
              ? "Answering"
              : turn.state === "queued_behind_live_turn"
                ? "Held"
                : "Queued";
          return (
            <li key={turn.turn_id} className="brief-chat-turn">
              <div className="brief-chat-question">
                <p className="eyebrow">
                  Question ·{" "}
                  <time dateTime={turn.created_at}>
                    {new Date(turn.created_at).toLocaleString("en-GB", {
                      timeZone: "UTC",
                    })}{" "}
                    UTC
                  </time>
                </p>
                <p>{turn.message}</p>
              </div>
              <div className="brief-chat-answer">
                <p className="eyebrow" role="status">
                  {state}
                </p>
                {answer !== null ? (
                  <p>{answer}</p>
                ) : terminal ? (
                  <p>
                    {terminal.type === "complete"
                      ? "This turn finished without a reply. Check Timeline for its details."
                      : `${typeof terminal.payload.message === "string" ? terminal.payload.message : "The reply stopped before completing."} Review the evidence before sending a new question.`}
                  </p>
                ) : exited ? (
                  <p>
                    {state === "Answered"
                      ? "This turn finished. Check Timeline for its reply."
                      : "The reply stopped before completing. Review the evidence before sending a new question."}
                  </p>
                ) : (
                  <p>
                    {state === "Held"
                      ? "Held — waiting for the current segment to finish."
                      : state === "Queued"
                        ? "Queued — waiting for the runtime to begin."
                        : "The investigation is answering this question. Its reply will appear here."}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
      {readOnly ? null : (
        <form
          className="brief-chat-compose stack stack--tight"
          onSubmit={(event) => void submit(event)}
        >
          <label htmlFor="chat-question">Your question</label>
          <textarea
            className="input"
            id="chat-question"
            value={draft}
            rows={4}
            disabled={sending}
            aria-invalid={error ? true : undefined}
            aria-describedby={`chat-help${error ? " chat-error" : ""}`}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
          />
          <p id="chat-help" className="muted">
            Up to 12,000 characters. Live reasoning is not kept in this
            conversation.
          </p>
          {error ? (
            <p id="chat-error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            className="btn btn--primary"
            type="submit"
            disabled={sending || loading}
          >
            {sending ? "Sending question…" : "Send question"}
          </button>
        </form>
      )}
    </div>
  );
}
function merge(current: ChatTurn[], incoming: ChatTurn[]) {
  const turns = new Map(current.map((turn) => [turn.turn_id, turn]));
  for (const turn of incoming) turns.set(turn.turn_id, turn);
  return [...turns.values()].sort(
    (a, b) =>
      a.created_at.localeCompare(b.created_at) ||
      a.turn_id.localeCompare(b.turn_id),
  );
}
function readFailure(reason: unknown) {
  if (reason instanceof ApiError && reason.status === 401)
    return "Your session has expired. Reload the page to sign in again.";
  if (reason instanceof ApiError && reason.status === 404)
    return "This conversation is unavailable in this workspace. Return to investigations or select the workspace that holds it.";
  return `${messageOf(reason)} Try reading the conversation again.`;
}
